import axios from 'axios';
import jsPDF from 'jspdf';

const apiKey = process.env.REACT_APP_X_API_KEY;
const appBaseUrl = process.env.REACT_APP_API_ADMIN_BASE_URL;

const getAccountStatusAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'API-KEY': apiKey
  };
};

const getSettlementStatusAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const partnerId = localStorage.getItem('pid');
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (partnerId) {
    headers['partner-id'] = partnerId;
  }
  
  return headers;
};

const downloadPDF = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const generateAccountStatusReceiptPDF = (transactionData) => {
  if (!transactionData || !transactionData.orderId) {
    throw new Error('Transaction data is required');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPosition = margin;

  const formatAmount = (amount) => {
    if (!amount || amount === 'N/A' || amount === null || amount === undefined) {
      return 'N/A';
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formattedNumber = numAmount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `₦${formattedNumber}`;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') {
      return 'N/A';
    }
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'success': 'Success',
      'failed': 'Failed',
      'reversed': 'Reversed',
      'not_found': 'Not Found',
      'pending': 'Pending',
      'server_error': 'Server Error',
      'unauthorized': 'Unauthorized'
    };
    return statusMap[status] || status || 'Unknown';
  };

  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('Transaction Receipt', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  const data = transactionData.data?.data || {};
  const amount = transactionData.status === 'reversed' 
    ? data.amount_paid 
    : data.amount;

  const items = [
    { label: 'Order ID', value: transactionData.orderId || 'N/A' },
    { label: 'Status', value: getStatusLabel(transactionData.status) },
    { label: 'Amount', value: formatAmount(amount) },
    { label: 'Merchant ID', value: data.merchantId || 'N/A' },
    { label: 'Payer Name', value: data.payer_account_name || 'N/A' },
    { label: 'Payer Bank', value: data.payer_bank || 'N/A' },
    { label: 'Payment Date', value: formatDate(data.paid_at) },
    { label: 'Details', value: data.details || 'N/A' }
  ];

  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');

  items.forEach((item, index) => {
    if (item.label === 'Details' && item.value !== 'N/A') {
      yPosition += 5;
      doc.setFont(undefined, 'bold');
      doc.text(`${item.label}:`, margin, yPosition);
      yPosition += 7;
      doc.setFont(undefined, 'normal');
      
      const splitDetails = doc.splitTextToSize(item.value, pageWidth - (2 * margin));
      splitDetails.forEach((line) => {
        if (yPosition > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(line, margin + 5, yPosition);
        yPosition += 6;
      });
    } else {
      if (yPosition > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFont(undefined, 'bold');
      doc.text(`${item.label}:`, margin, yPosition);
      
      const valueLines = doc.splitTextToSize(String(item.value), pageWidth - margin - 80);
      doc.setFont(undefined, 'normal');
      
      valueLines.forEach((line, lineIndex) => {
        doc.text(line, margin + 70, yPosition + (lineIndex * 6));
      });
      
      yPosition += Math.max(valueLines.length * 6, 10);
    }
  });

  yPosition += 10;
  if (yPosition > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    yPosition = margin;
  }

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  const footerText1 = 'This is an automated receipt generated by NetPlus Payment System.';
  const footerText2 = 'For support, contact: support@netpluspay.com';
  
  doc.text(footerText1, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 7;
  doc.text(footerText2, pageWidth / 2, yPosition, { align: 'center' });

  const filename = `account_status_${transactionData.orderId}.pdf`;
  doc.save(filename);
  
  return { success: true };
};

export const exportAccountStatusPDF = async (orderId, transactionData = null) => {
  if (transactionData) {
    try {
      generateAccountStatusReceiptPDF(transactionData);
      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'PDF generation failed');
    }
  }

  try {
    const response = await axios.post(
      `${appBaseUrl}/transactions/account-status/pdf`,
      { orderId },
      {
        headers: getAccountStatusAuthHeaders(),
        responseType: 'blob'
      }
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    downloadPDF(blob, `account_status_${orderId}.pdf`);
    
    return { success: true };
  } catch (error) {
    if (error.response?.data) {
      const errorBlob = new Blob([error.response.data], { type: 'application/json' });
      const errorText = await errorBlob.text();
      const errorData = JSON.parse(errorText);
      throw new Error(errorData.message || 'PDF export failed');
    }
    
    throw new Error(error.message || 'PDF export failed');
  }
};

export const generateSettlementStatusReceiptPDF = (settlementData) => {
  if (!settlementData || !settlementData.data) {
    throw new Error('Settlement data is required');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPosition = margin;

  const formatAmount = (amount) => {
    if (!amount || amount === 'N/A' || amount === null || amount === undefined) {
      return 'N/A';
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formattedNumber = numAmount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `₦${formattedNumber}`;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') {
      return 'N/A';
    }
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'confirmed': 'Confirmed',
      'processing': 'Processing',
      'failed': 'Failed',
      'not_found': 'Not Found',
      'server_error': 'Server Error',
      'unauthorized': 'Unauthorized'
    };
    return statusMap[status] || status || 'Unknown';
  };

  const getSourceTableLabel = (sourceTable) => {
    switch (sourceTable) {
      case 'bank_transactions': return 'Confirmed Transaction';
      case 'pending_transactions': return 'Pending Transaction';
      case 'failed_transactions': return 'Failed Transaction';
      default: return 'Unknown Source';
    }
  };

  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('Settlement Receipt', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  const data = settlementData.data || {};

  const items = [
    { label: 'Transaction Reference', value: data.transaction_reference || 'N/A' },
    { label: 'Status', value: getStatusLabel(data.status) },
    { label: 'Amount', value: formatAmount(data.amount) },
    { label: 'Charges', value: formatAmount(data.charges) },
    { label: 'Transaction Type', value: data.transaction_type ? data.transaction_type.charAt(0).toUpperCase() + data.transaction_type.slice(1) : 'N/A' },
    { label: 'Destination Account', value: data.destination_account || 'N/A' },
    { label: 'Destination Account Name', value: data.destination_account_name || 'N/A' },
    { label: 'Destination Bank', value: data.destination_bank || 'N/A' },
    { label: 'Partner ID', value: data.partner_id || 'N/A' },
    { label: 'Created At', value: formatDate(data.created_at) }
  ];

  if (data.provider_payment_id) {
    items.splice(1, 0, { label: 'Provider Payment ID', value: data.provider_payment_id });
  }

  if (settlementData.source_table) {
    const sourceTableLabel = getSourceTableLabel(settlementData.source_table);
    items.splice(2, 0, { label: 'Source', value: sourceTableLabel });
  }

  if (data.status === 'failed') {
    if (data.response_message) {
      items.push({ label: 'Response Message', value: data.response_message });
    }
    if (data.narration) {
      items.push({ label: 'Description', value: data.narration });
    }
  }

  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');

  items.forEach((item) => {
    if (yPosition > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      yPosition = margin;
    }

    doc.setFont(undefined, 'bold');
    doc.text(`${item.label}:`, margin, yPosition);
    
    const valueLines = doc.splitTextToSize(String(item.value), pageWidth - margin - 80);
    doc.setFont(undefined, 'normal');
    
    valueLines.forEach((line, lineIndex) => {
      doc.text(line, margin + 75, yPosition + (lineIndex * 6));
    });
    
    yPosition += Math.max(valueLines.length * 6, 10);
  });

  yPosition += 10;
  if (yPosition > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    yPosition = margin;
  }

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 15;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  const footerText1 = 'This is an automated receipt generated by NetPlus Payment System.';
  const footerText2 = 'For support, contact: support@netpluspay.com';
  
  doc.text(footerText1, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 7;
  doc.text(footerText2, pageWidth / 2, yPosition, { align: 'center' });

  const identifier = data.transaction_reference || data.provider_payment_id || 'settlement';
  const filename = `settlement_status_${identifier}.pdf`;
  doc.save(filename);
  
  return { success: true };
};

export const exportSettlementStatusPDF = async (transactionReference, providerPaymentId = null, settlementData = null) => {
  if (settlementData) {
    try {
      generateSettlementStatusReceiptPDF(settlementData);
      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'PDF generation failed');
    }
  }

  try {
    const body = {};
    if (transactionReference) body.transactionReference = transactionReference;
    if (providerPaymentId) body.provider_payment_id = providerPaymentId;

    const response = await axios.post(
      `${appBaseUrl}/transactions/settlement-status/pdf`,
      body,
      {
        headers: getSettlementStatusAuthHeaders(),
        responseType: 'blob'
      }
    );

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const filename = `settlement_status_${transactionReference || providerPaymentId}.pdf`;
    downloadPDF(blob, filename);
    
    return { success: true };
  } catch (error) {
    if (error.response?.data) {
      const errorBlob = new Blob([error.response.data], { type: 'application/json' });
      const errorText = await errorBlob.text();
      const errorData = JSON.parse(errorText);
      throw new Error(errorData.message || 'PDF export failed');
    }
    
    throw new Error(error.message || 'PDF export failed');
  }
};
